import { Box, Checkbox, CircularProgress, Grid } from "@mui/material";
import IndeterminateIcon from "components/IndeterminateIcon";
import MDTypography from "components/MDTypography";
import { useCallback, useEffect, useMemo, useState } from "react";
import RowPillar from "./RowPillar";
import MDBox from "components/MDBox";
import { useLanguage } from "contexts/language.context";
import MDButton from "components/MDButton";

function refresherProps(pillar) {
  const props = {};
  pillar.kpis.forEach((kpi, index) => {
    props[`selected ${index}`] = kpi.selected;
    props[`description ${index}`] = kpi.description;
    props[`weight ${index}`] = kpi.weight;
    props[`eliminatory ${index}`] = kpi.eliminatory;
  });
  return props;
}

export default function PillarsTable({
  pillars,
  include = { checkbox: true },
  onChangeSelected,
  onManualChangeSelected,
  updatableDescription,
  updatableWeight,
  updatableEliminatory,
  initial,
  tableContainerProps,
  loading,
  defaultExpanded,
  firstRowExpanded,
  selectAllSignal,
}) {
  const [localPillars, setLocalPillars] = useState(pillars);
  const [checkbox, setCheckbox] = useState({
    checked: false,
    indeterminate: false,
  });
  const { label, languages } = useLanguage();

  useEffect(() => {
    if (!initial)
      setLocalPillars(
        pillars.map((pillar) => {
          return {
            ...pillar,
            kpis: pillar.kpis.map((kpi) => {
              return {
                ...kpi,
                initialAllLanguageDescription: kpi.allLanguageDescription,
                allLanguageDescription: kpi.allLanguageDescription,
              };
            }),
          };
        }),
      );
    else {
      const newLocalPillars = JSON.parse(JSON.stringify(pillars));

      newLocalPillars.forEach((pillar, pillarIndex) => {
        pillar.kpis.forEach((kpi, kpiIndex) => {
          const foundElement = initial?.find(
            (element) => element.kpiId == kpi.id,
          );

          if (foundElement) {
            newLocalPillars[pillarIndex].kpis[kpiIndex].selected = true;

            newLocalPillars[pillarIndex].kpis[kpiIndex].description =
              foundElement.description ??
              newLocalPillars[pillarIndex].kpis[kpiIndex].description;

            newLocalPillars[pillarIndex].kpis[
              kpiIndex
            ].initialAllLanguageDescription =
              newLocalPillars[pillarIndex].kpis[
                kpiIndex
              ].allLanguageDescription;

            newLocalPillars[pillarIndex].kpis[kpiIndex].allLanguageDescription =
              foundElement.allLanguageDescription ??
              newLocalPillars[pillarIndex].kpis[kpiIndex]
                .allLanguageDescription;

            newLocalPillars[pillarIndex].kpis[kpiIndex].weight =
              foundElement.weight ?? kpi.weight;

            newLocalPillars[pillarIndex].kpis[kpiIndex].eliminatory =
              foundElement.eliminatory ?? kpi.eliminatory;
          } else {
            newLocalPillars[pillarIndex].kpis[kpiIndex].selected = false;
            newLocalPillars[pillarIndex].kpis[
              kpiIndex
            ].initialAllLanguageDescription =
              newLocalPillars[pillarIndex].kpis[
                kpiIndex
              ].allLanguageDescription;
          }
        });
      });

      setLocalPillars(newLocalPillars);
    }
  }, [pillars, initial]);

  useEffect(() => {
    let allSelected = true;
    let noneSelected = true;

    localPillars?.forEach((pillar) => {
      pillar.kpis?.forEach((kpi) => {
        if (kpi.selected) {
          noneSelected = false;
        } else {
          allSelected = false;
        }
      });
    });

    if (allSelected) {
      setCheckbox({ checked: true, indeterminate: false });
    } else if (noneSelected) {
      setCheckbox({ checked: false, indeterminate: false });
    } else {
      setCheckbox({ checked: false, indeterminate: true });
    }
  }, [
    ...localPillars?.flatMap((pillar) =>
      pillar.kpis.map((kpi) => Boolean(kpi.selected)),
    ),
    initial,
  ]);

  const handleSelectPillar = useCallback(
    (pillarIndex) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis.forEach((_, index) => {
          newPillars[pillarIndex].kpis[index].selected = true;
        });
        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }
        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleDeselectPillar = useCallback(
    (pillarIndex) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis.forEach((_, index) => {
          newPillars[pillarIndex].kpis[index].selected = false;
        });
        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }
        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleSelectKpi = useCallback(
    (pillarIndex, kpiIndex) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis[kpiIndex].selected = true;
        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }
        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleDeselectKpi = useCallback(
    (pillarIndex, kpiIndex) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis[kpiIndex].selected = false;
        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }
        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleSelectAll = useCallback(
    (props) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars.forEach((_, pillarIndex) => {
          newPillars[pillarIndex].kpis.forEach((_, kpiIndex) => {
            newPillars[pillarIndex].kpis[kpiIndex].selected = true;
          });
        });

        if (!props?.ignoreManualChange && onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }

        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleFillEmptyWithInitial = useCallback(
    (props) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars.forEach((_, pillarIndex) => {
          newPillars[pillarIndex].kpis.forEach((_, kpiIndex) => {
            if (!newPillars[pillarIndex].kpis[kpiIndex]?.selected) return;
            const keys = languages.map((l) => l.id);
            keys.forEach((key) => {
              if (
                !newPillars[pillarIndex].kpis[kpiIndex].allLanguageDescription[
                  key
                ]
              ) {
                newPillars[pillarIndex].kpis[kpiIndex].allLanguageDescription[
                  key
                ] =
                  newPillars[pillarIndex].kpis[
                    kpiIndex
                  ].initialAllLanguageDescription[key];
              }
            });
          });
        });

        if (!props?.ignoreManualChange && onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  initialAllLanguageDescription:
                    kpi.initialAllLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }

        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleDeselectAll = useCallback(() => {
    setLocalPillars((localPillars) => {
      const newPillars = [...localPillars];
      newPillars.forEach((_, pillarIndex) => {
        newPillars[pillarIndex].kpis.forEach((_, kpiIndex) => {
          newPillars[pillarIndex].kpis[kpiIndex].selected = false;
        });
      });

      if (onManualChangeSelected) {
        onManualChangeSelected(
          newPillars?.flatMap((pillar) =>
            pillar.kpis
              .filter((kpi) => kpi.selected)
              .map((kpi) => ({
                kpiId: kpi.id,
                description: kpi.description,
                allLanguageDescription: kpi.allLanguageDescription,
                weight: kpi.weight,
                eliminatory: kpi.eliminatory,
              })),
          ),
        );
      }

      return newPillars;
    });
  }, [setLocalPillars]);

  const handleChangeKpiDescription = useCallback(
    (pillarIndex, kpiIndex, description) => {
      setLocalPillars((localPillars) => {
        const newPillars = localPillars.map((pillar, pIndex) => {
          if (pIndex !== pillarIndex) return pillar;

          return {
            ...pillar,
            kpis: pillar.kpis.map((kpi, kIndex) => {
              if (kIndex !== kpiIndex) return kpi;

              return {
                ...kpi,
                allLanguageDescription: description,
              };
            }),
          };
        });
        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }

        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleChangeKpiWeight = useCallback(
    (pillarIndex, kpiIndex, weight) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis[kpiIndex].weight = weight;

        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }

        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const handleChangeKpiEliminatory = useCallback(
    (pillarIndex, kpiIndex, eliminatory) => {
      setLocalPillars((localPillars) => {
        const newPillars = [...localPillars];
        newPillars[pillarIndex].kpis[kpiIndex].eliminatory = eliminatory;

        if (onManualChangeSelected) {
          onManualChangeSelected(
            newPillars?.flatMap((pillar) =>
              pillar.kpis
                .filter((kpi) => kpi.selected)
                .map((kpi) => ({
                  kpiId: kpi.id,
                  description: kpi.description,
                  allLanguageDescription: kpi.allLanguageDescription,
                  weight: kpi.weight,
                  eliminatory: kpi.eliminatory,
                })),
            ),
          );
        }

        return newPillars;
      });
    },
    [setLocalPillars],
  );

  const utils = useMemo(() => {
    return {
      handleSelectPillar,
      handleDeselectPillar,
      handleSelectKpi,
      handleDeselectKpi,
      handleChangeKpiDescription,
      handleChangeKpiWeight,
      handleChangeKpiEliminatory,
    };
  }, [
    handleSelectPillar,
    handleDeselectPillar,
    handleSelectKpi,
    handleDeselectKpi,
    handleChangeKpiDescription,
    handleChangeKpiWeight,
    handleChangeKpiEliminatory,
  ]);

  useEffect(() => {
    if (onChangeSelected) {
      onChangeSelected(
        localPillars?.flatMap((pillar) =>
          pillar.kpis
            .filter((kpi) => kpi.selected)
            .map((kpi) => ({
              kpiId: kpi.id,
              description: kpi.description,
              allLanguageDescription: kpi.allLanguageDescription,
              weight: kpi.weight,
              eliminatory: kpi.eliminatory,
            })),
        ),
      );
    }
  }, [localPillars]);

  useEffect(() => {
    if (selectAllSignal) {
      handleSelectAll({ ignoreManualChange: true });
    }
  }, [selectAllSignal]);

  return (
    <>
      {updatableDescription || updatableWeight || updatableEliminatory ? (
        <MDButton
          onClick={() =>
            handleFillEmptyWithInitial({ ignoreManualChange: false })
          }
          variant="gradient"
          color="info"
          size="small"
          sx={{ alignSelf: "end", justifySelf: "end" }}
        >
          {label("fill-empty-translations-from-initial-values")}
        </MDButton>
      ) : null}
      <Grid container sx={{ width: "100%" }} alignItems="center">
        <Grid
          item
          xs
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
          }}
        >
          {include?.checkbox ? (
            <Checkbox
              onChange={(e, checked) => {
                if (checked) {
                  handleSelectAll();
                } else {
                  handleDeselectAll();
                }
              }}
              checked={checkbox.checked}
              indeterminate={checkbox.indeterminate}
              indeterminateIcon={<IndeterminateIcon />}
            />
          ) : (
            <Box sx={{ width: "20px" }}></Box>
          )}
          <MDTypography
            sx={{ fontSize: "0.9rem", fontWeight: "500", width: "100%" }}
          >
            {label("name-singular")}
          </MDTypography>
        </Grid>
        {include?.weight && (
          <Grid item xs={2}>
            <MDTypography
              sx={{ fontSize: "0.9rem", fontWeight: "500", width: "100%" }}
            >
              {label("weight-singular")}
            </MDTypography>
          </Grid>
        )}
        {include?.eliminatory && (
          <Grid item xs={2}>
            <MDTypography
              sx={{ fontSize: "0.9rem", fontWeight: "500", width: "100%" }}
            >
              Eliminatory
            </MDTypography>
          </Grid>
        )}
        {include?.categories && (
          <Grid item xs={4.25}>
            <MDTypography
              sx={{ fontSize: "0.9rem", fontWeight: "500", width: "100%" }}
            >
              {label("category-singular")}
            </MDTypography>
          </Grid>
        )}
        {include?.pillar?.create &&
          include?.pillar?.update &&
          include?.pillar?.delete &&
          include?.kpi?.create &&
          include?.kpi?.update &&
          include?.kpi?.delete && <Grid item sx={{ width: "130px" }}></Grid>}
      </Grid>

      {!loading ? (
        <Grid
          container
          {...tableContainerProps}
          sx={{ overflow: "auto", ...tableContainerProps?.sx }}
        >
          {localPillars.map((pillar, pillarIndex) => {
            return (
              <RowPillar
                key={"Pillar: " + pillar.id}
                defaultExpanded={
                  defaultExpanded || (firstRowExpanded && pillarIndex == 0)
                }
                pillar={pillar}
                pillarIndex={pillarIndex}
                utils={utils}
                include={include}
                updatableDescription={updatableDescription}
                updatableWeight={updatableWeight}
                updatableEliminatory={updatableEliminatory}
                {...refresherProps(pillar)}
              />
            );
          })}
        </Grid>
      ) : (
        <MDBox
          sx={{
            width: "100%",
            minHeight: "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="info" size={30} />
        </MDBox>
      )}
    </>
  );
}
